<!--
SubscriptionTermAcceptanceModal — one acceptance, in full.

This is the evidence view: the agreement text EXACTLY as it stood when signed,
the signature image, who signed, from where, and the two hashes.

The text shown is the SNAPSHOT stored on the acceptance row, never the current
document — and it is the LANGUAGE VARIANT the signer actually read, which the
row records alongside the version they thereby agreed to. That is the whole point — the document may have been edited, retired
or deleted since, and showing today's text next to a past signature would
misrepresent what was agreed.

The hashes are displayed rather than hidden because they are the cheap
integrity check: the content hash can be compared against the published version
to prove it was not edited afterwards.
-->
<template>
  <modal
    v-if="isVisible"
    elem-id="modal_subscription_term_acceptance"
    modal-size="modal-lg"
    :title="t('Aceite registrado')"
    :show="isVisible"
    :close-label="t('Fechar')"
    close-test-id="subscription-term-acceptance-modal-close-btn"
    :hide-save="true"
    data-testid="subscription-term-acceptance-modal"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <div v-if="isLoading" class="loading-container" data-testid="subscription-term-acceptance-modal-loading">
      <spinner style="width: 32px; height: 32px;" />
    </div>

    <div v-else-if="errorCode" class="acceptance-error" data-testid="subscription-term-acceptance-modal-error">
      <i class="pi pi-exclamation-triangle"></i>
      {{ errorCode }}
    </div>

    <template v-else-if="row">
      <dl class="acceptance-facts">
        <div><dt>{{ t('Franquia') }}</dt><dd data-testid="acceptance-franquia">{{ row.franquia_nome || `#${row.franquia_id}` }}</dd></div>
        <div><dt>{{ t('Assinado por') }}</dt><dd data-testid="acceptance-signer">{{ row.accepted_by_email ?? row.accepted_by_sub ?? '—' }}</dd></div>
        <div><dt>{{ t('Quando') }}</dt><dd data-testid="acceptance-when">{{ row.created_at ?? '—' }}</dd></div>
        <div><dt>{{ t('Endereço IP') }}</dt><dd data-testid="acceptance-ip">{{ row.ip_address ?? '—' }}</dd></div>
        <div><dt>{{ t('Termo') }}</dt><dd>{{ row.termo_titulo }} — {{ t('versão') }} {{ row.termo_versao }} ({{ row.termo_plano }} · {{ row.termo_idioma }})</dd></div>
        <div><dt>{{ t('Idioma lido') }}</dt><dd data-testid="acceptance-language">{{ row.termo_idioma }}</dd></div>
        <div><dt>{{ t('Método') }}</dt><dd data-testid="acceptance-method">{{ row.assinatura_metodo === 'digitada' ? t('Nome digitado') : t('Assinatura desenhada') }}</dd></div>
      </dl>

      <section class="acceptance-section">
        <h4>{{ t('Assinatura') }}</h4>
        <img
          v-if="assinaturaPng"
          :src="assinaturaPng"
          :alt="t('Assinatura')"
          class="acceptance-signature"
          data-testid="acceptance-signature-image"
        />
        <p v-else class="acceptance-muted">{{ t('Sem imagem de assinatura.') }}</p>
      </section>

      <section class="acceptance-section">
        <h4>{{ t('Texto assinado') }}</h4>
        <p class="acceptance-muted">
          {{ t('Cópia guardada no momento da assinatura — não é o texto atual do termo.') }}
        </p>
        <div class="acceptance-body" data-testid="acceptance-body" v-html="sanitizedBody"></div>
      </section>

      <section class="acceptance-section">
        <h4>{{ t('Conferência de integridade') }}</h4>
        <p class="acceptance-hash" data-testid="acceptance-content-hash">
          {{ t('Texto') }}: <code>{{ row.content_sha256 }}</code>
        </p>
        <p class="acceptance-hash" data-testid="acceptance-signature-hash">
          {{ t('Assinatura') }}: <code>{{ row.assinatura_sha256 }}</code>
        </p>
        <p v-if="userAgent" class="acceptance-muted" data-testid="acceptance-user-agent">
          {{ t('Navegador') }}: {{ userAgent }}
        </p>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { sanitizeRichText } from '../../../utils/sanitizeRichText'
import { useSubscriptionTerms } from '../../../composables/useSubscriptionTerms'
import type { SubscriptionTerms_AcceptanceRow } from '../../../contracts/SubscriptionTerms_AcceptanceRow'

const { t } = useI18n()
const { getAcceptance } = useSubscriptionTerms()

const isVisible = ref(false)
const isLoading = ref(false)
const errorCode = ref<string | null>(null)
const row = ref<SubscriptionTerms_AcceptanceRow | null>(null)
const termoConteudo = ref('')
const assinaturaPng = ref('')
const userAgent = ref<string | null>(null)

/**
 * The stored body is author-written HTML from the staff editor, cleaned by the
 * platform's ALLOWLIST sanitiser before rendering.
 *
 * sanitizeRichText, not a hand-rolled denylist: this text is replayed into an
 * audit screen possibly years after it was stored, and a denylist only blocks
 * the attacks whoever wrote it happened to think of.
 */
const sanitizedBody = computed(() => sanitizeRichText(termoConteudo.value))

function closeModal(): void {
  isVisible.value = false
  row.value = null
  termoConteudo.value = ''
  assinaturaPng.value = ''
  userAgent.value = null
  errorCode.value = null
}

async function openModal(payload?: { id?: number }): Promise<void> {
  const id = payload?.id
  if (id == null) return

  isVisible.value = true
  isLoading.value = true
  errorCode.value = null

  try {
    const dados = await getAcceptance(id)
    row.value = dados.row
    termoConteudo.value = dados.termo_conteudo
    assinaturaPng.value = dados.assinatura_png
    userAgent.value = dados.user_agent
  } catch (err) {
    errorCode.value = err instanceof Error ? err.message : String(err)
    log('SubscriptionTermAcceptanceModal', 'openModal', 'Erro', errorCode.value)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.subscription-term-acceptance.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.subscription-term-acceptance.open', openModal)
})
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  padding: 32px;
}

.acceptance-error {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--p-red-500);
}

.acceptance-facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
  margin: 0 0 20px;
}

.acceptance-facts div {
  display: flex;
  flex-direction: column;
}

.acceptance-facts dt {
  font-size: 12px;
  color: var(--p-text-muted-color, #6b7280);
}

.acceptance-facts dd {
  margin: 0;
  font-size: 14px;
}

.acceptance-section {
  margin-bottom: 20px;
}

.acceptance-section h4 {
  margin: 0 0 8px;
  font-size: 14px;
}

.acceptance-signature {
  max-width: 100%;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
  background: #fff;
}

.acceptance-body {
  max-height: 260px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
}

.acceptance-muted {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 12px;
  margin: 0 0 6px;
}

.acceptance-hash {
  font-size: 12px;
  margin: 0 0 4px;
  word-break: break-all;
}
</style>
